import { apiClient as api } from './api';

export interface CustomerInfo {
  TraderCode: string;
  TraderName: string;
  address1: string;
  address2: string;
  address3: string;
  tel: string;
  Email: string;
  DelName: string;
  Anl: string;
}

export interface CheckoutRequest {
  customerInfo: {
    address1: string;
    address2: string;
    address3: string;
    tel: string;
    email: string;
    traderName?: string;
  };
  cartItems: Array<{
    SupersedeNo: string;
    UOM: string;
    Description: string;
    Qty: number;
    Price: number;
    ConvRate: number;
  }>;
  cartSummary: {
    subtotal: number;
    deliveryAmount: number;
    gstAmount: number;
    grandTotal: number;
    gstFlag: string;
    gstPercent: number;
    discountAmt?: number;
  };
  priceLevel: string;
  // Razorpay verification triple, present when a card payment was made.
  razorpayOrderId?: string;
  razorpayPaymentId?: string;
  razorpaySignature?: string;
  paymentMethod?: 'card' | 'wallet';
  promoCode?: string;
}

export interface CheckoutResponse {
  success: boolean;
  quotationRefNo?: string;
  message: string;
}

export interface CustomerInfoResponse {
  success: boolean;
  data?: CustomerInfo;
  message?: string;
}

export interface TermsResponse {
  success: boolean;
  data?: {
    term1: string;
    term2: string;
    term3: string;
    terms?: string[];
  };
  message?: string;
}

export interface UserQuotation {
  RefNo: string;
  TraderName: string;
  DateIssued: string;
  TotalDueAmt: number;
  GSTAmt: number;
  webStatus: string;
}

export interface QuotationStatusHistoryEntry {
  HistoryID: string;
  RefNo: string;
  FromStatus: string | null;
  ToStatus: string;
  Notes: string | null;
  NotesEmailedToCustomer: boolean;
  ChangedBy: string | null;
  ChangedAt: string;
}

export interface QuotationDetail extends UserQuotation {
  items?: Array<{
    RefNo: string;
    LineNO: number;
    SupersedeNo: string;
    UOM: string;
    Description: string;
    Qty: number;
    Price: number;
    ItemNetAmt: number;
  }>;
  CompId?: string;
  TraderCode?: string;
  CurrencyCode?: string;
  webAddress1?: string;
  webAddress2?: string;
  webAddress3?: string;
  webTel?: string;
  webEmail?: string;
  webUserID?: string;
  statusHistory?: QuotationStatusHistoryEntry[];
}

export class QuotationService {
  private static getErrorMessage(error: unknown, fallback: string): string {
    const apiError = error as {
      message?: string;
      response?: {
        data?: {
          message?: string;
          errors?: Array<{ message?: string }>;
        };
      };
    };

    return (
      apiError.response?.data?.errors?.[0]?.message ||
      apiError.response?.data?.message ||
      apiError.message ||
      fallback
    );
  }

  /**
   * Get customer information for checkout
   */
  static async getCustomerInfo(): Promise<CustomerInfoResponse> {
    try {
      const response = await api.get('/quotations/customer-info');
      return response;
    } catch (error) {
      console.error('Error getting customer info:', error);
      return {
        success: false,
        message: 'Failed to retrieve customer information'
      };
    }
  }

  /**
   * Get terms and conditions for a given context:
   *   'checkout' → placing an order (default)
   *   'topup'    → wallet top-up page
   */
  static async getTermsAndConditions(
    context: 'checkout' | 'topup' = 'checkout'
  ): Promise<TermsResponse> {
    try {
      // apiClient.get takes the query params object directly as the 2nd arg
      // (it wraps them as axios `params` internally) — not an axios config.
      const response = await api.get('/quotations/terms', { context });
      return response;
    } catch (error) {
      console.error('Error getting terms:', error);
      return {
        success: false,
        message: 'Failed to retrieve terms and conditions'
      };
    }
  }

  /**
   * Process checkout and create quotation
   */
  static async processCheckout(checkoutData: CheckoutRequest): Promise<CheckoutResponse> {
    try {
      const response = await api.post('/quotations/checkout', checkoutData);
      return response;
    } catch (error) {
      console.error('Error processing checkout:', error);
      return {
        success: false,
        message: QuotationService.getErrorMessage(error, 'Failed to process checkout')
      };
    }
  }

  /**
   * Process guest checkout
   */
  static async processGuestCheckout(checkoutData: CheckoutRequest): Promise<CheckoutResponse> {
    try {
      const response = await api.post('/quotations/guest-checkout', checkoutData);
      return response;
    } catch (error) {
      console.error('Error processing guest checkout:', error);
      return {
        success: false,
        message: QuotationService.getErrorMessage(error, 'Failed to process guest checkout')
      };
    }
  }

  /**
   * Get user's quotations (for profile page)
   */
  static async getUserQuotations(userId?: string): Promise<UserQuotation[]> {
    try {
      const response = await api.get('/quotations/user', {
        params: userId ? { userId } : undefined
      });
      return response.data || [];
    } catch (error) {
      console.error('Failed to get user quotations:', error);
      return [];
    }
  }

  /**
   * Get quotation details
   */
  static async getQuotationDetail(refNo: string): Promise<QuotationDetail | null> {
    try {
      const response = await api.get(`/quotations/${refNo}`);
      return response.data || null;
    } catch (error) {
      console.error('Failed to get quotation details:', error);
      return null;
    }
  }
}

// Export singleton instance
export const quotationService = new QuotationService();
